import ReactEChartsCore from 'echarts-for-react/lib/core';
import { echarts, BRAND_COLOR, baseChartOptions } from '../../shared/echarts-config';
import { ChartContainer } from '../../shared/ChartContainer';
import { EmptyState } from '../../shared/EmptyState';
import type { FpRatePoint } from '../../../../types/analytics.types';

type AdvisorEngagementRateChartProps = {
  points: FpRatePoint[];
};

const TITLE = 'Advisor Engagement Rate';
const SUBTITLE = 'Share of advisors active per period';

const buildChartOptions = (points: FpRatePoint[]) => ({
  ...baseChartOptions,
  tooltip: {
    trigger: 'axis' as const,
    formatter: (params: Array<{ dataIndex: number }>) => {
      const idx = params[0]?.dataIndex;
      if (idx == null) return '';
      const point = points[idx];
      if (!point) return '';

      const dateStr = new Date(point.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });

      let html = `<div style="font-weight:600;margin-bottom:6px">${dateStr}</div>`;
      html += `<div style="display:flex;justify-content:space-between;gap:16px">`;
      html += `<span>Engagement Rate</span><span style="font-weight:600">${point.rate}%</span>`;
      html += `</div>`;
      html += `<div style="display:flex;justify-content:space-between;gap:16px;font-size:12px;color:#6b7280">`;
      html += `<span>Active Advisors</span><span style="color:#374151">${point.activeAdvisors}</span>`;
      html += `</div>`;
      return html;
    },
  },
  xAxis: {
    type: 'category' as const,
    data: points.map((p) => p.date),
    axisLabel: {
      formatter: (value: string) => {
        const d = new Date(value);
        return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
      },
      fontSize: 12,
      color: '#6b7280',
    },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
  },
  yAxis: {
    type: 'value' as const,
    min: 0,
    max: 100,
    axisLabel: { formatter: '{value}%', fontSize: 12, color: '#6b7280' },
    splitLine: { lineStyle: { color: '#f3f4f6' } },
  },
  series: [
    {
      name: 'Engagement Rate',
      type: 'line',
      data: points.map((p) => p.rate),
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 2.5, color: BRAND_COLOR },
      itemStyle: { color: BRAND_COLOR },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(61, 101, 96, 0.25)' },
            { offset: 1, color: 'rgba(61, 101, 96, 0.02)' },
          ],
        },
      },
    },
  ],
});

export const AdvisorEngagementRateChart = ({ points }: AdvisorEngagementRateChartProps) => {
  if (points.length === 0) {
    return (
      <ChartContainer title={TITLE} subtitle={SUBTITLE}>
        <EmptyState message="No engagement data available for the selected date range." />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer title={TITLE} subtitle={SUBTITLE}>
      <ReactEChartsCore
        echarts={echarts}
        option={buildChartOptions(points)}
        style={{ height: 300 }}
        notMerge
        lazyUpdate
      />
    </ChartContainer>
  );
};
